<script setup lang="ts">
import { computed, provide, useId } from 'vue';
import { FIELD_CONTEXT_KEY } from './fieldContext';

// The field wrapper carries the validation message (issue 195, ADR 0068 rule 4).
// The server has been naming the offending field since ADR 0036; the mutation seam
// turns that into a `path -> message` map, and this is where the message lands —
// under the control it is about, never in a toast that cannot point at one of eight
// inputs.
//
// Two ways to supply the message, so a form does not have to spell out eight lookups:
//   <Field :label="…" :error="localMessage" />                     // client-side rule
//   <Field :label="…" name="email" :errors="fieldErrors" />        // the seam's map
// `error` wins when both are given: a message the call site computed deliberately is
// more specific than a map lookup.
//
// `name` also marks the wrapper with `data-field-path`, which is how a form moves focus
// to the first offending control after a failed submit without knowing the markup.
const props = withDefaults(
  defineProps<{
    label?: string;
    hint?: string;
    error?: string;
    /** The envelope path this field maps to, e.g. `email`. */
    name?: string;
    /** The seam's `fieldErrors` map, looked up by `name`. */
    errors?: Record<string, string>;
    required?: boolean;
    /** The id of a control that already carries its own: the label binds to it instead of the generated one. */
    for?: string;
  }>(),
  { required: false },
);

// The label's `for`. The lib's own controls (Input, Select, Textarea, DateField, Combobox,
// MultiCombobox) inject the generated id and take it when the consumer gave them none —
// the first one to ask only, so two controls in one Field never share an id. A control
// that carries its own id is bound with the `for` prop; a hand-made element can still
// take `<template #default="{ id }">`.
const generatedId = useId();
const controlId = computed(() => props.for || generatedId);

let owner: symbol | null = null;
provide(FIELD_CONTEXT_KEY, {
  claim(who) {
    if (props.for) return undefined;
    if (owner && owner !== who) return undefined;
    owner = who;
    return generatedId;
  },
  release(who) {
    if (owner === who) owner = null;
  },
});

const message = computed(
  () => props.error || (props.name ? props.errors?.[props.name] : undefined) || '',
);
</script>

<template>
  <div class="lumi-field" :class="{ 'lumi-field--invalid': message }" :data-field-path="name">
    <label v-if="label" class="lumi-field__label" :for="controlId">
      {{ label }}<span v-if="required" class="lumi-field__req">*</span>
    </label>
    <slot :invalid="!!message" :id="controlId" />
    <span v-if="message" class="lumi-field__error" role="alert">{{ message }}</span>
    <span v-else-if="hint" class="lumi-field__hint">{{ hint }}</span>
  </div>
</template>

<style>
/* Spec §8: labels 13.5 px semibold above the field, helper text tertiary,
   validation in error text under the field. */
.lumi-field{display:flex;flex-direction:column;gap:6px;font-family:var(--font-body);}
.lumi-field__label{font-size:var(--text-label);font-weight:var(--weight-semibold);color:var(--text-heading);}
.lumi-field__req{color:var(--danger-text);margin-left:2px;}
.lumi-field__hint{font-size:var(--text-xs);color:var(--text-tertiary);}
.lumi-field__error{font-size:var(--text-xs);color:var(--danger-text);}
.lumi-field--invalid input,.lumi-field--invalid select,.lumi-field--invalid textarea{border-color:var(--danger);}
</style>
